import {
    useEffect,
    useRef,
    useState,
} from 'react';
import { Dropdown } from '@honeycomb-npm/honeycomb-react';

import {
    GenericBladeParsedDataType,
    GenericFormAEM,
    NodePayload,
} from '../genericBladeTypes';
import GenericBladeFormHeading from './BladeFormHeading';
import { useAppDispatch } from '../../../app/hooks';
import { genericBladeDropDownChange } from '../genericBladeSlice';

type PropsT = {
    id: string;
    genericFormAEM: Record<string, GenericFormAEM>;
    genericFormApiData?: GenericBladeParsedDataType;
    handleChange: (node: string, value: NodePayload) => void;
    errors: Record<string, string>;
};

type BlurT = {
    message: string;
    highlightError: boolean;
};

export const BladeDropdown = ({
    id,
    genericFormAEM,
    genericFormApiData,
    handleChange,
    errors,
}: PropsT): JSX.Element => {
    const dispatch = useAppDispatch();
    const [selectedValue, setSelectedValue] = useState<
        Record<string, NodePayload>
    >(
        Object.entries(genericFormApiData ?? {}).reduce((result, [key, data]) => (
            (data.value) ? { ...result, [key]: data.value } : result
        ), {} as Record<string, NodePayload>),
    );

    const dropdownRef = useRef<Record<string, HTMLSelectElement | null>>(
        Object.keys(genericFormAEM).reduce((result, key) => ({
            ...result,
            [key]: null,
        }), {} as Record<string, null>),
    );

    const [blurMessage, setBlurMessage] = useState<Record<string, BlurT>>(
        Object.entries(genericFormAEM).reduce((result, [key]) => ({
            ...result,
            [key]: {
                message: '',
                highlightError: false,
            },
        }), {} as Record<string, BlurT>),
    );

    useEffect(() => {
        if (errors) {
            const blurMessages: Record<string, BlurT> = {};

            Object.entries(errors).forEach(([key, value]) => {
                blurMessages[key] = blurMessage[key];
                blurMessages[key].message = value;
                blurMessages[key].highlightError = true;
            });
            setBlurMessage((msg) => ({
                ...msg,
                ...blurMessages,
            }));

            for (const key of Object.keys(genericFormAEM)) {
                if (errors[key]) {
                    dropdownRef.current?.[key]?.focus();
                    break;
                }
            }
        }
    }, [errors]);

    const handleEditValue = (node: string, newValue: NodePayload) => {
        setSelectedValue((value) => ({
            ...value,
            [node]: newValue,
        }));
        setBlurMessage((msg) => ({
            ...msg,
            [node]: newValue
                ? {
                    ...msg[node],
                    highlightError: false,
                    message: '',
                }
                : {
                    highlightError: true,
                    message: '',
                },
        }));
        handleChange(node, newValue);
        dispatch(genericBladeDropDownChange({
            id: node,
            selectedValue: newValue,
        }));
    };

    const handleBlur = () => {
        const activeElement = document.activeElement;

        if (activeElement?.tagName === 'BUTTON') return;

        Object.keys(genericFormAEM).map((key) => {
            if (!selectedValue[key]) {
                setBlurMessage((msg) => ({
                    ...msg,
                    [key]: {
                        ...msg[key],
                        highlightError: true,
                    },
                }));
            } else {
                setBlurMessage((msg) => ({
                    ...msg,
                    [key]: {
                        ...msg[key],
                        highlightError: false,
                    },
                }));
            }
        });
    };

    return (
        <>
            {Object.entries(genericFormAEM).map(([node, content]) => (
                <div key={node}>
                    <GenericBladeFormHeading
                        {...{
                            id,
                            node,
                            heading: content.heading,
                            description: content.description,
                        }}
                    />
                    <div className="p-0 col-12 col-md-5 mb-4">
                        <Dropdown
                            id={`generic-blade-dropdown-${id}-${node}`}
                            name={`generic-blade-dropdown-${id}-${node}`}
                            defaultOption="Select"
                            options={
                                genericFormApiData?.[node]?.options ??
                                content?.dropdownOptions?.map(({ key, label, value }) => ({
                                    label: key || label || '',
                                    value,
                                }))
                            }
                            onChange={(_: string, newValue: NodePayload) =>
                                handleEditValue(node, newValue)
                            }
                            value={selectedValue?.[node] ?? ''}
                            label={content.label}
                            errormessage={blurMessage[node].message}
                            isValid={!blurMessage[node].highlightError}
                            hideDefaultOption={!!selectedValue?.[node]}
                            onBlur={() => handleBlur()}
                            ref={(el: HTMLSelectElement) =>
                                (dropdownRef.current[node] = el)
                            }
                        />
                    </div>
                    {content.showFormDivider && (
                        <hr aria-hidden="true" className="mb-4" />
                    )}
                </div>
            ))}
        </>
    );
};
